import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import {
  getClasses,
  getStudentsByClass,
  getActiveTerms,
  generateReport,
} from "../api/services";

function AdminReports() {
  const [classes, setClasses] = useState([]);
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [terms, setTerms] = useState([]);

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInitial();
  }, []);

  const fetchInitial = async () => {
    try {
      const [classRes, termRes] = await Promise.all([
        getClasses(),
        getActiveTerms(),
      ]);

      setClasses(classRes.data);
      setTerms(termRes.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load data");
    }
  };

  const handleClassChange = async (classId) => {
    setSelectedClass(classId);
    setSelectedStudent("");

    if (!classId) {
      setStudents([]);
      return;
    }

    try {
      const res = await getStudentsByClass(classId);
      setStudents(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to load students");
    }
  };

  const openReport = async (studentId, termId) => {
    try {
      const token = localStorage.getItem("access");
      const url = generateReport(studentId, termId);

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const pdfUrl = window.URL.createObjectURL(blob);

      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Report generation warning:", error);
    }
  };

  const handleGenerateSingle = async () => {
    if (!selectedStudent || !selectedTerm) {
      alert("Please select a student and term.");
      return;
    }

    setLoading(true);
    await openReport(selectedStudent, selectedTerm);
    setLoading(false);
  };

  const handleGenerateClass = async () => {
    if (!selectedClass || !selectedTerm) {
      alert("Please select a class and term.");
      return;
    }

    if (students.length === 0) {
      alert("No students found in this class.");
      return;
    }

    if (!window.confirm("Generate report cards for the entire class?"))
      return;

    setLoading(true);

    for (const student of students) {
      await openReport(student.id, selectedTerm);
      await new Promise((r) => setTimeout(r, 350));
    }

    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>{t("academicReportGenerator")}</h1>
        <p style={styles.subtitle}>
          {t("academicReportGeneratorText")}
        </p>
      </div>

      <div style={styles.card}>
        <div style={styles.field}>
          <label style={styles.label}>{t("class")}</label>
          <select
            style={styles.select}
            value={selectedClass}
            onChange={(e) => handleClassChange(e.target.value)}
          >
            <option value="">-- Select Class --</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
    {c["department__name"]
        ? `${c.name} (${c["department__name"]})`
        : c.name}
</option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>{t("student")}</label>
          <select
            style={styles.select}
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
          >
            <option value="">-- Select Student --</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.first_name} {s.last_name}
              </option>
            ))}
          </select>

          {students.length > 0 && (
            <span style={styles.studentCount}>
              {students.length} student(s) in this class
            </span>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>{t("term")}</label>
          <select
            style={styles.select}
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <option value="">-- Select Term --</option>
            {terms.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {t.session__name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.actions}>
          <button
            style={styles.primaryBtn}
            onClick={handleGenerateSingle}
            disabled={loading}
          >
            {loading ? t("generating") : t("generateStudentReport")}
          </button>

          <button
            style={styles.bulkBtn}
            onClick={handleGenerateClass}
            disabled={loading}
          >
            {loading ? t("processing") : t("generateEntireClass")}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "40px",
    fontFamily: "Inter, Segoe UI, sans-serif",
    background: "linear-gradient(135deg,#eef2ff,#f8fafc)",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  header: {
    textAlign: "center",
    marginBottom: "30px",
  },

  title: {
    fontSize: "30px",
    fontWeight: "700",
    color: "#1e293b",
  },

  subtitle: {
    color: "#64748b",
    marginTop: "6px",
  },

  card: {
    background: "white",
    padding: "35px",
    borderRadius: "18px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.08)",
    width: "100%",
    maxWidth: "700px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "20px",
  },

  label: {
    marginBottom: "6px",
    fontWeight: "600",
    color: "#334155",
  },

  select: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
  },

  studentCount: {
    marginTop: "5px",
    fontSize: "12px",
    color: "#64748b",
  },

  actions: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "15px",
  },

  primaryBtn: {
    padding: "13px 22px",
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
  },

  bulkBtn: {
    padding: "13px 22px",
    background: "#16a34a",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
  },
};

export default AdminReports;